/**
 * StudentDeviceCheck — le test du matériel, posé dans les pièces de la salle d'attente : le flux de la
 * caméra qui remplit la grande image (`StudentCameraFeed`), et l'étape ouverte (`StudentDeviceCheckStep`,
 * `<DeviceCheckStep>` sans aperçu — l'image est déjà là, en grand), son écoute
 * (`<DeviceCheckSoundTest>`) et les barres du micro.
 *
 * Il ne décide rien : les props viennent du view-model (`deviceCheckViewModel`), les gestes du
 * crochet du moteur (`useDeviceCheckMachine`).
 */
import { DeviceCheckSoundTest, DeviceCheckStep } from "../../../../../../design-system/student"
import React, { useEffect, useState } from "react"
import { View } from "react-native"

import CameraPreview from "../../../../../../engine/precall/CameraPreview"
import { type DeviceCheckController } from "../../../../../../engine/precall/useDeviceCheckMachine"
import { type DeviceCheckVM } from "../../../../view-models/student/LiveEntry/deviceCheckViewModel"

// L'aperçu natif ne choisit pas d'objectif (`CameraView`, caméra avant) ; les deux disent leur échec (`onDeviceError`).
const Preview = CameraPreview as unknown as React.ComponentType<{
    deviceId: string | null
    onDeviceError: DeviceCheckController["onPreviewError"]
}>

/** Le flux de la caméra, dans la grande image. L'analyse de l'image lit le flux sous cette vue (`previewRef`). */
export function StudentCameraFeed({ check }: { check: DeviceCheckController }) {
    return (
        <View ref={check.previewRef as React.RefObject<View>} style={{ flex: 1 }}>
            <Preview deviceId={check.videoDeviceId} onDeviceError={check.onPreviewError} />
        </View>
    )
}

/** Le niveau qui sort pendant la mélodie : un battement, comme l'ancien `OutputLevel`. */
function useChimeLevel(playing: boolean): number {
    const [tick, setTick] = useState(0)
    useEffect(() => {
        if (!playing) return undefined
        const timer = setInterval(() => setTick((n) => n + 1), 90)
        return () => clearInterval(timer)
    }, [playing])
    return playing ? 0.35 + 0.5 * Math.abs(Math.sin(tick / 2)) : 0
}

type StepProps = { vm: DeviceCheckVM; check: DeviceCheckController }

/** L'étape ouverte du test, sous la rangée du matériel. Rien quand aucune n'est ouverte. */
export function StudentDeviceCheckStep({ vm, check }: StepProps) {
    const { step } = vm
    const chimeLevel = useChimeLevel(step?.soundTest?.state === "playing")
    if (step == null) return null

    return (
        <DeviceCheckStep
            line={step.line}
            resolvedText={step.resolvedText}
            soundTest={
                step.soundTest != null ? (
                    <DeviceCheckSoundTest
                        state={step.soundTest.state}
                        level={chimeLevel}
                        pointer={step.soundTest.pointer}
                        blockedBy={step.soundTest.blockedBy}
                        onPlay={check.playChime}
                        onAnswer={step.soundTest.answerable ? check.answerSpeaker : undefined}
                    />
                ) : undefined
            }
            listening={step.listening}
            micLevel={step.showsMicLevel ? check.micLevel : undefined}
            guidance={step.guidance}
            actions={step.actions}
            onAction={(key) => onAction(check, key)}
            devices={
                step.devices != null
                    ? {
                          options: step.devices.options,
                          selectedId: step.devices.selectedId,
                          onSelect: (id) => check.selectDevice(step.devices!.kind, id),
                      }
                    : undefined
            }
            onNextAttempt={step.nextAttempt ? check.nextAttempt : undefined}
            onSkip={() => check.skip(step.line.id)}
        />
    )
}

/** Les gestes de l'étape (`precallActions`), vers le crochet. */
function onAction(check: DeviceCheckController, key: string) {
    if (key === "authorize") check.authorize()
    else if (key === "open-settings") check.openSettings()
    else if (key === "mute-video") check.setVideoMuted(true)
    else if (key === "enable-video") check.setVideoMuted(false)
    else if (key === "mic-no-signal") check.micNoSignal()
    else if (key === "sees-self-yes") check.answerSeesSelf(true)
    else if (key === "sees-self-no") check.answerSeesSelf(false)
}
